import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Box,
    Fade,
    Chip,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Paper,
} from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { intl, fetchData, HTTP_STATUS } from 'dmsbasereact';
import { DmsVideoPlayer } from '../DmsVideoPlayer/DmsVideoPlayer';
import { openCertificarePdfForPrint } from '../../utils/downloadCertificatePdf';
import { AttendanceUploadModal } from '../AttendanceUploadModal/AttendanceUploadModal';

const ACCOUNT_STATUS = {
    PENDING_RECYCLING_TRAINING: 10,
};

/**
 * Presentation Component: Contains the entire UI for the training experience.
 * It is fully responsive and manages the state of the video interaction.
 */
export const DmsTrainingVideo = ({
    training, videoUrl, isTraining, materialPk,
    completionDate, certificateExpiry,
    isMainTraining = false, accountStatus = null,
    trainingData = null,
}) => {
    const navigate = useNavigate();

    const needsRecycling = accountStatus === ACCOUNT_STATUS.PENDING_RECYCLING_TRAINING;
    const wasAlreadyCompleted = !!completionDate && !needsRecycling;

    const [isCompleted, setIsCompleted] = useState(wasAlreadyCompleted);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [videoDuration, setVideoDuration] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [showAttendanceModal, setShowAttendanceModal] = useState(false);
    const [requiresAttendance, setRequiresAttendance] = useState(false);
    const [markedWatched, setMarkedWatched] = useState(false);
    const [currentTrainingData, setCurrentTrainingData] = useState(trainingData);
    
    const markedWatchedRef = useRef(false);
    const latestProgressRef = useRef(0);
    const materialsRef = useRef(trainingData?.materials || []);

    // Atualiza a referência dos materiais quando trainingData muda
    useEffect(() => {
        materialsRef.current = trainingData?.materials || [];
        setCurrentTrainingData(trainingData);
    }, [trainingData]);

    // Determine if all mandatory materials are watched (for multi-material trainings)
    const allMandatoryWatched = currentTrainingData ? currentTrainingData.all_mandatory_watched : true;
    const materials = currentTrainingData?.materials || [];
    const hasMultipleMaterials = materials.length > 1;
    const trainingPk = currentTrainingData?.training_pk || training?.pk;

    // Atualiza o estado do vídeo assistido com base no trainingData
    useEffect(() => {
        if (materialPk && materials.length > 0) {
            const currentMaterial = materials.find(m => String(m.material_pk) === String(materialPk));
            if (currentMaterial) {
                setMarkedWatched(currentMaterial.watched || false);
                markedWatchedRef.current = currentMaterial.watched || false;
            }
        }
    }, [materialPk, materials]);

    useEffect(() => {
        const checkAttendanceRequirement = async () => {
            if (!materialPk) return;
            try {
                const response = await fetchData(
                    `/training_material/${materialPk}/requires-attendance-upload`,
                    'GET',
                    true
                );
                if (response.status === HTTP_STATUS.OK_200 && response.data?.requires_attendance_upload) {
                    setRequiresAttendance(true);
                }
            } catch (err) {
                console.error('Error checking attendance requirement:', err);
            }
        };
        checkAttendanceRequirement();
    }, [materialPk]);

    // Função para atualizar os dados do treinamento
    const refreshTrainingData = useCallback(async () => {
        if (!trainingPk) return;
        
        try {
            const response = await fetchData('/training_material/my-trainings', 'GET', true);
            const trainingsData = response.data?.trainings || [];
            const updatedTrainingData = trainingsData.find(t => t.training_pk === trainingPk);
            
            if (updatedTrainingData) {
                setCurrentTrainingData(updatedTrainingData);
                materialsRef.current = updatedTrainingData.materials || [];
                
                // Atualiza o estado do vídeo atual
                const currentMaterial = updatedTrainingData.materials?.find(
                    m => String(m.material_pk) === String(materialPk)
                );
                if (currentMaterial) {
                    setMarkedWatched(currentMaterial.watched || false);
                    markedWatchedRef.current = currentMaterial.watched || false;
                }
            }
        } catch (err) {
            console.error('Error refreshing training data:', err);
        }
    }, [trainingPk, materialPk]);

    // Função para marcar vídeo como assistido
    const markVideoAsWatched = useCallback(async () => {
        if (!materialPk || markedWatchedRef.current) return;
        
        try {
            markedWatchedRef.current = true;
            setMarkedWatched(true);
            
            await fetchData(`/training_material/${materialPk}/mark-watched`, 'POST', true);
            
            // Atualiza os dados do treinamento
            await refreshTrainingData();
            
            // Se for um treinamento com múltiplos materiais, navega para o próximo não assistido
            if (hasMultipleMaterials && materialsRef.current.length > 0) {
                const currentIndex = materialsRef.current.findIndex(
                    m => String(m.material_pk) === String(materialPk)
                );
                
                // Procura o próximo material obrigatório não assistido
                let nextUnwatched = materialsRef.current.find((m, idx) => 
                    idx > currentIndex && 
                    m.is_mandatory && 
                    !m.watched
                );
                
                // Se não encontrar obrigatório, procura qualquer material não assistido
                if (!nextUnwatched) {
                    nextUnwatched = materialsRef.current.find((m, idx) => 
                        idx > currentIndex && 
                        !m.watched
                    );
                }
                
                // Se não encontrar depois do atual, procura do início
                if (!nextUnwatched) {
                    nextUnwatched = materialsRef.current.find(m => !m.watched && String(m.material_pk) !== String(materialPk));
                }
                
                if (nextUnwatched) {
                    // Pequeno delay para garantir que o estado foi atualizado
                    setTimeout(() => {
                        navigate(`/training-video/${nextUnwatched.material_pk}`);
                    }, 500);
                }
            }
        } catch (err) {
            console.error('Error marking as watched:', err);
            markedWatchedRef.current = false;
            setMarkedWatched(false);
        }
    }, [materialPk, hasMultipleMaterials, navigate, refreshTrainingData]);

    // Monitora o progresso para marcar automaticamente
    useEffect(() => {
        const checkAndMarkWatched = () => {
            if (latestProgressRef.current >= 95 && 
                !markedWatchedRef.current && 
                materialPk && 
                !wasAlreadyCompleted &&
                !isCompleted) {
                markVideoAsWatched();
            }
        };

        // Verifica a cada segundo se o progresso atingiu 95%
        const interval = setInterval(checkAndMarkWatched, 1000);
        return () => clearInterval(interval);
    }, [materialPk, wasAlreadyCompleted, isCompleted, markVideoAsWatched]);

    const handleProgressChange = (progress) => {
        latestProgressRef.current = progress;
    };

    const handleCompleteButtonClick = () => {
        if (requiresAttendance) {
            setShowAttendanceModal(true);
        } else {
            handleCompleteTraining(null);
        }
    };

    const handleAttendanceModalConfirm = (attendanceFilePk) => {
        setShowAttendanceModal(false);
        handleCompleteTraining(attendanceFilePk);
    };

    const handleCompleteTraining = async (attendanceFilePk) => {
        if (!materialPk) {
            setSnackbar({
                open: true,
                message: intl.get("TrainingCompletionError").d("Error completing training. Please try again."),
                severity: 'error'
            });
            return;
        }

        // Primeiro, garante que o vídeo atual está marcado como assistido
        if (!markedWatchedRef.current && !wasAlreadyCompleted) {
            try {
                await fetchData(`/training_material/${materialPk}/mark-watched`, 'POST', true);
                markedWatchedRef.current = true;
                setMarkedWatched(true);
                // Atualiza os dados
                await refreshTrainingData();
            } catch (err) {
                console.error('Error marking current video as watched:', err);
            }
        }

        setIsSubmitting(true);
        try {
            const requestBody = attendanceFilePk ? { attendance_file_pk: attendanceFilePk } : {};
            await fetchData(`/training_material/${materialPk}/complete`, 'POST', true, requestBody);
            setIsCompleted(true);
            setSnackbar({
                open: true,
                message: intl.get("TrainingCompletedSuccess").d("Training completed successfully! Your certificate has been issued."),
                severity: 'success'
            });
            window.dispatchEvent(new CustomEvent('trainingCompleted'));

            try {
                await openCertificarePdfForPrint(trainingPk);
                setTimeout(() => {
                    window.location.href = '/home';
                }, 2500);
            } catch (error) {
                console.error('Error downloading certificate:', error);
            }
        } catch (err) {
            console.error('Error completing training:', err);
            setSnackbar({
                open: true,
                message: err.message || intl.get("TrainingCompletionError").d("Error completing training. Please try again."),
                severity: 'error'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const formatTime = (timeInSeconds) => {
        if (isNaN(timeInSeconds) || timeInSeconds === 0) return "00:00";
        const date = new Date(0);
        date.setSeconds(timeInSeconds);
        return date.toISOString().substr(timeInSeconds >= 3600 ? 11 : 14, timeInSeconds >= 3600 ? 8 : 5);
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    // Determina se o botão "Completar Treinamento" deve estar habilitado
    const canComplete = allMandatoryWatched && !isCompleted && !isSubmitting;

    // Atualiza automaticamente os dados a cada 5 segundos
    useEffect(() => {
        if (!wasAlreadyCompleted && !isCompleted) {
            const interval = setInterval(() => {
                refreshTrainingData();
            }, 5000);
            
            return () => clearInterval(interval);
        }
    }, [wasAlreadyCompleted, isCompleted, refreshTrainingData]);

    return (
        <Box sx={{
            height: '100vh',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f5f5f7',
            overflow: 'hidden'
        }}>
            <Fade in={true} timeout={1000}>
                <div style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    {/* Compact Header */}
                    <Fade in={true} timeout={1200} style={{ transitionDelay: '200ms' }}>
                        <Box sx={{
                            flexShrink: 0,
                            py: 1.5,
                            px: 3,
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 0.5,
                                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                                        textShadow: '0 1px 5px rgba(0,0,0,0.3)',
                                        letterSpacing: '-0.01em'
                                    }}
                                >
                                    {training.name}
                                </Typography>

                                {training.description && (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'rgba(255,255,255,0.9)',
                                            mb: 1,
                                            fontSize: { xs: '0.85rem', md: '0.95rem' },
                                            maxWidth: '500px',
                                            mx: 'auto',
                                            fontWeight: 300,
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {training.description}
                                    </Typography>
                                )}

                                <Chip
                                    icon={<AccessTime sx={{ color: 'white !important' }} />}
                                    label={`${intl.get("Duration").d("Duration")}: ${formatTime(videoDuration)}`}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        '& .MuiChip-icon': { color: 'white !important' },
                                        px: 1.5,
                                        py: 0.5
                                    }}
                                />

                                {wasAlreadyCompleted && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Chip
                                            icon={<CheckCircleOutlineIcon sx={{ color: 'white !important' }} />}
                                            label={`${intl.get("FinishedAt").d("Finished at")}: ${formatDateTime(completionDate)}, ${intl.get("ValidUntil").d("Valid Until")}: ${formatDateTime(certificateExpiry)}`}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                borderColor: 'rgba(76, 175, 80, 0.7)',
                                                color: 'white',
                                                backgroundColor: 'rgba(76, 175, 80, 0.3)',
                                                '& .MuiChip-icon': { color: 'white !important' },
                                                px: 1.5,
                                                py: 0.5
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Indicador de vídeo assistido */}
                                {markedWatched && !wasAlreadyCompleted && (
                                    <Box sx={{ mt: 1 }}>
                                        <Chip
                                            icon={<CheckCircleOutlineIcon sx={{ color: 'white !important' }} />}
                                            label={intl.get("VideoWatched").d("Video watched")}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                borderColor: 'rgba(255, 193, 7, 0.7)',
                                                color: 'white',
                                                backgroundColor: 'rgba(255, 193, 7, 0.3)',
                                                '& .MuiChip-icon': { color: 'white !important' },
                                                px: 1.5,
                                                py: 0.5
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Fade>

                    {/* Main content area: video + optional sidebar */}
                    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                        {/* Video container and controls */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <DmsVideoPlayer 
                                videoUrl={videoUrl} 
                                onDurationChange={setVideoDuration} 
                                isTraining={isTraining}
                                onProgressChange={handleProgressChange}
                            >
                                {(videoState, controls) => {
                                    // Atualiza o progresso no ref
                                    if (videoState.progress !== undefined) {
                                        latestProgressRef.current = videoState.progress;
                                    }

                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}>
                                            <Box sx={{ minWidth: '160px' }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontWeight: 500, mb: 0.3, fontSize: '0.8rem' }}
                                                >
                                                    {intl.get("Progress").d("Video Progress")}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    component="div"
                                                    sx={{
                                                        fontWeight: 600,
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        backgroundClip: 'text',
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent',
                                                        fontSize: '0.95rem'
                                                    }}
                                                >
                                                    {videoState.formatTime(videoState.currentTime)} / {videoState.formatTime(videoState.duration)}
                                                </Typography>
                                                <Typography variant="body2" color="primary" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                                    {Math.round(videoState.progress)}% {intl.get("completed").d("completed")}
                                                </Typography>
                                            </Box>

                                            {wasAlreadyCompleted ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ fontSize: '0.85rem' }}
                                                    >
                                                        {intl.get("TrainingCompletedOn").d("Training completed on")} {formatDateTime(completionDate)}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        startIcon={<DownloadIcon />}
                                                        onClick={() => openCertificarePdfForPrint(trainingPk)}
                                                        size="medium"
                                                        sx={{
                                                            borderRadius: '20px',
                                                            textTransform: 'none',
                                                            fontWeight: '600',
                                                            px: 3, py: 1,
                                                            fontSize: '0.9rem',
                                                            background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                                                            boxShadow: '0 6px 20px rgba(76,175,80,0.3)',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 25px rgba(76,175,80,0.4)' }
                                                        }}
                                                    >
                                                        {intl.get("DownloadCertificate").d("Download Certificate")}
                                                    </Button>
                                                </Box>
                                            ) : !hasMultipleMaterials ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        color={isCompleted ? "success" : "primary"}
                                                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : (isCompleted ? <CheckCircleOutlineIcon /> : null)}
                                                        onClick={handleCompleteButtonClick}
                                                        disabled={!canComplete}
                                                        size="medium"
                                                        sx={{
                                                            borderRadius: '20px',
                                                            textTransform: 'none',
                                                            fontWeight: '600',
                                                            px: 3, py: 1,
                                                            fontSize: '0.9rem',
                                                            background: isCompleted
                                                                ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                                                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                            boxShadow: '0 6px 20px rgba(102,126,234,0.3)',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 25px rgba(102,126,234,0.4)' },
                                                            '&:disabled': {
                                                                background: isCompleted
                                                                    ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                                                                    : 'rgba(0, 0, 0, 0.12)'
                                                            }
                                                        }}
                                                    >
                                                        {isSubmitting
                                                            ? intl.get("Completing").d("Completing...")
                                                            : isCompleted
                                                                ? intl.get("Completed").d("Completed")
                                                                : intl.get("CompleteTraining").d("Complete Training")
                                                        }
                                                    </Button>
                                                </Box>
                                            ) : null}
                                        </Box>
                                    );
                                }}
                            </DmsVideoPlayer>
                        </Box>

                        {/* Sidebar: materials list for multi-material trainings */}
                        {hasMultipleMaterials && (
                            <Paper
                                elevation={2}
                                sx={{
                                    width: 280,
                                    flexShrink: 0,
                                    overflow: 'auto',
                                    borderLeft: '1px solid',
                                    borderColor: 'divider',
                                    display: { xs: 'none', md: 'block' },
                                }}
                            >
                                <Box sx={{ p: 1.5, backgroundColor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {intl.get("VideosWatched").d("Videos watched")}: {currentTrainingData?.watched_count || 0}/{currentTrainingData?.mandatory_count || 0}
                                    </Typography>
                                    {!allMandatoryWatched && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {currentTrainingData?.mandatory_count - (currentTrainingData?.watched_count || 0)} {intl.get("mandatoryVideosLeft").d("mandatory videos left")}
                                        </Typography>
                                    )}
                                </Box>
                                <List dense disablePadding>
                                    {materials.map((material, index) => {
                                        const isCurrent = String(material.material_pk) === String(materialPk);
                                        const isWatched = material.watched || (isCurrent && markedWatched);
                                        return (
                                            <React.Fragment key={material.material_pk}>
                                                <ListItem
                                                    sx={{
                                                        px: 1.5, py: 1,
                                                        cursor: isCurrent ? 'default' : 'pointer',
                                                        backgroundColor: isCurrent ? 'primary.50' : 'transparent',
                                                        borderLeft: isCurrent ? '3px solid' : '3px solid transparent',
                                                        borderColor: isCurrent ? 'primary.main' : 'transparent',
                                                        '&:hover': { backgroundColor: isCurrent ? 'primary.50' : 'action.hover' }
                                                    }}
                                                    onClick={() => {
                                                        if (!isCurrent) navigate(`/training-video/${material.material_pk}`);
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        {isWatched ? (
                                                            <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                                                        ) : isCurrent ? (
                                                            <PlayCircleOutlineIcon color="primary" sx={{ fontSize: 18 }} />
                                                        ) : (
                                                            <RadioButtonUncheckedIcon color="disabled" sx={{ fontSize: 18 }} />
                                                        )}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={material.file_name}
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                            sx: {
                                                                fontWeight: isCurrent ? 600 : 400,
                                                                fontSize: '0.78rem',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }
                                                        }}
                                                        secondary={
                                                            <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, mt: 0.3 }}>
                                                                {material.is_mandatory && (
                                                                    <Chip size="small"
                                                                        label={intl.get("Mandatory").d("Mandatory")}
                                                                        color="error" variant="outlined"
                                                                        sx={{ height: 16, fontSize: '0.6rem' }} />
                                                                )}
                                                                {isWatched && (
                                                                    <Chip size="small"
                                                                        label={intl.get("Watched").d("Watched")}
                                                                        color="success" variant="outlined"
                                                                        sx={{ height: 16, fontSize: '0.6rem' }} />
                                                                )}
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                                {index < materials.length - 1 && <Divider component="li" />}
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                                <Divider />
                                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    {!allMandatoryWatched && !isCompleted && (
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                            {intl.get("AllMandatoryMustBeWatched").d("All mandatory materials must be watched before completing the training")}
                                        </Typography>
                                    )}
                                    {allMandatoryWatched && !isCompleted && (
                                        <Typography variant="caption" color="success.main" sx={{ textAlign: 'center', fontWeight: 600 }}>
                                            ✓ {intl.get("AllMandatoryWatched").d("All mandatory videos have been watched")}
                                        </Typography>
                                    )}
                                    {!wasAlreadyCompleted && (
                                        <Button
                                            variant="contained"
                                            color={isCompleted ? "success" : "primary"}
                                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : (isCompleted ? <CheckCircleOutlineIcon /> : null)}
                                            onClick={handleCompleteButtonClick}
                                            disabled={!canComplete}
                                            fullWidth
                                            size="medium"
                                            sx={{
                                                borderRadius: '20px',
                                                textTransform: 'none',
                                                fontWeight: '600',
                                                py: 1,
                                                fontSize: '0.9rem',
                                                background: isCompleted
                                                    ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                boxShadow: '0 6px 20px rgba(102,126,234,0.3)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 25px rgba(102,126,234,0.4)' },
                                                '&:disabled': {
                                                    background: isCompleted
                                                        ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                                                        : 'rgba(0, 0, 0, 0.12)'
                                                }
                                            }}
                                        >
                                            {isSubmitting
                                                ? intl.get("Completing").d("Completing...")
                                                : isCompleted
                                                    ? intl.get("Completed").d("Completed")
                                                    : intl.get("CompleteTraining").d("Complete Training")
                                            }
                                        </Button>
                                    )}
                                    {wasAlreadyCompleted && (
                                        <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={<DownloadIcon />}
                                            onClick={() => openCertificarePdfForPrint(trainingPk)}
                                            fullWidth
                                            size="medium"
                                            sx={{
                                                borderRadius: '20px',
                                                textTransform: 'none',
                                                fontWeight: '600',
                                                py: 1,
                                                fontSize: '0.9rem',
                                                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                                                boxShadow: '0 6px 20px rgba(76,175,80,0.3)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 25px rgba(76,175,80,0.4)' }
                                            }}
                                        >
                                            {intl.get("DownloadCertificate").d("Download Certificate")}
                                        </Button>
                                    )}
                                </Box>
                            </Paper>
                        )}
                    </Box>
                </div>
            </Fade>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <AttendanceUploadModal
                open={showAttendanceModal}
                onClose={() => setShowAttendanceModal(false)}
                onConfirm={handleAttendanceModalConfirm}
            />
        </Box>
    );
};